// finds the name (or name of store) details needed based on fulfillment type
// will return the name of the person (pickup person if applicable) and store (if applicable)

// currently only tested for STH, BOSS, BOPIS
import ShippingIcon from '../../public/images/svg/Fulfillment_Ship.svg';
import PickupInStoreIcon from '../../public/images/svg/Bopis_Boss.svg';
import BodfsIcon from '../../public/images/svg/Express_Truck.svg';
import ApplianceIcon from '../../public/images/svg/Fulfillment_Appliance.svg';
import InStoreIcon from '../../public/images/svg/In_Store.svg';

import safelyAccessNestedProperties from './safelyAccessNestedProperties';
import { FULFILLMENTS } from '../constants';
import { STATUS } from '../constants/status';

export const findNameByFulfillment = (
  fulfillmentType: string,
  fulfillment: Object
) => {
  const fulfillmentMeta = {
    shiptohome: {
      namePath: ['shippingAndHandling', 'deliveryAddress', 'name'],
      storeNamePath: [null],
    },
    buyonlineshiptostore: {
      namePath: [
        'bossShippingAndHandling',
        'pickUpDetails',
        'pickUpPerson',
        'name',
      ],
      storeNamePath: [
        'bossShippingAndHandling',
        'pickUpDetails',
        'store',
        'name',
      ],
    },
    buyonlinepickupinstore: {
      namePath: ['pickUpDetails', 'pickUpPerson', 'name'],
      storeNamePath: ['pickUpDetails', 'store', 'name'],
    },
  }[fulfillmentType.toLowerCase()];

  const name = safelyAccessNestedProperties(
    fulfillmentMeta.namePath,
    fulfillment
  );

  const storeName = safelyAccessNestedProperties(
    fulfillmentMeta.storeNamePath,
    fulfillment
  );

  return {
    ...name,
    storeName,
  };
};

// finds the address details needed based on fulfillment type
// normalizes address response to deal with inconsistencies from api response
// e.g. difference between addressLine1 and line1

// currently only tested for STH, BOSS, BOPIS

export const findAddressByFulfillment = (
  fulfillmentType: string,
  fulfillment: Object
) => {
  const fulfillmentMeta = {
    shiptohome: {
      addressLine: 'addressLine',
      addressPath: ['shippingAndHandling', 'deliveryAddress', 'postalDetails'],
    },
    buyonlineshiptostore: {
      addressLine: 'line',
      addressPath: [
        'bossShippingAndHandling',
        'pickUpDetails',
        'store',
        'address',
      ],
    },
    buyonlinepickupinstore: {
      addressLine: 'line',
      addressPath: ['pickUpDetails', 'store', 'address'],
    },
  }[fulfillmentType.toLowerCase()];

  const address = safelyAccessNestedProperties(
    fulfillmentMeta.addressPath,
    fulfillment
  );

  return {
    addressLine1: address[`${fulfillmentMeta.addressLine}1`],
    addressLine2: address[`${fulfillmentMeta.addressLine}2`],
    city: address.city,
    state: address.state,
    zipCode: address.zipCode,
    country: address.country,
  };
};

export const findMethodByFulfillment = (fulfillmentType: string) =>
  ({
    shiptohome: 'sth',
    buyonlineshiptostore: 'boss',
    buyonlinepickupinstore: 'bopis',
  }[fulfillmentType.toLowerCase()]);

export const findZipCodeByFulfillment = (
  fulfillmentType: string,
  fulfillment: object
) => {
  const address = findAddressByFulfillment(fulfillmentType, fulfillment);
  return address ? address.zipCode : null;
};

export const findShippingAndHandlingByFulfillment = (
  fulfillmentType: String,
  fulfillment: object
) => {
  switch (fulfillmentType) {
    case FULFILLMENTS.FULFILLMENT_METHOD_STH:
      return fulfillment.shippingAndHandling;
    case FULFILLMENTS.FULFILLMENT_METHOD_BOSS:
      return fulfillment.bossShippingAndHandling;
    case FULFILLMENTS.FULFILLMENT_METHOD_BODFS:
    case FULFILLMENTS.FULFILLMENT_METHOD_DFS:
      return fulfillment.bodfsDeliveryAndHandling;
    case FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE:
      return fulfillment.applianceShippingAndHandling;
    case FULFILLMENTS.FULFILLMENT_METHOD_BOPIS:
      return fulfillment.pickupDetails;
    default: {
      return {};
    }
  }
};

export const setAddressLabelByFulfillmentAndStatus = (
  statusCode: Number,
  fulfillmentType: Object
) => {
  if (fulfillmentType === FULFILLMENTS.FULFILLMENT_METHOD_STH) {
    return statusCode === STATUS.DELIVERED ? 'Delivered to' : 'Delivering to';
  }
  if (fulfillmentType === FULFILLMENTS.FULFILLMENT_METHOD_BOSS) {
    return statusCode === STATUS.COMPLETED ? 'Picked up at' : 'Pickup at';
  }
  if (
    fulfillmentType === FULFILLMENTS.FULFILLMENT_METHOD_BODFS ||
    fulfillmentType === FULFILLMENTS.FULFILLMENT_METHOD_DFS
  ) {
    return statusCode === STATUS.DELIVERED ? 'Delivered to' : 'Delivering to';
  }
  if (fulfillmentType === FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE) {
    return statusCode === STATUS.COMPLETED || statusCode === STATUS.DELIVERED
      ? 'Delivered to'
      : 'Delivering to';
  }
  if (fulfillmentType === FULFILLMENTS.FULFILLMENT_METHOD_BOPIS) {
    return statusCode === STATUS.COMPLETED ? 'Picked up at' : 'Pickup at';
  }
  return 'Order to';
};

export const setFilterByFulfillment = (fulfillmentType: string) => {
  switch (fulfillmentType) {
    case FULFILLMENTS.FULFILLMENT_METHOD_BOSS:
      return 'bossTracking';
    case FULFILLMENTS.FULFILLMENT_METHOD_STH:
      return 'sthTracking';
    case FULFILLMENTS.FULFILLMENT_METHOD_BODFS:
    case FULFILLMENTS.FULFILLMENT_METHOD_DFS:
      return 'bodfsTracking';
    default: {
      console.error(
        `FulfillmentType: ${fulfillmentType} Unknown. Cannot set proper filter. Defaulting to STH`
      );
      return FULFILLMENTS.STH;
    }
  }
};

export const setShippingIconByFulfillmentMethod = (fulfillmentType: Object) => {
  switch (fulfillmentType) {
    case FULFILLMENTS.FULFILLMENT_METHOD_BOSS:
      return PickupInStoreIcon;
    case FULFILLMENTS.FULFILLMENT_METHOD_STH:
      return ShippingIcon;
    case FULFILLMENTS.FULFILLMENT_METHOD_BODFS:
    case FULFILLMENTS.FULFILLMENT_METHOD_DFS:
      return BodfsIcon; // TODO: EDIT THIS TO TRUCK
    case FULFILLMENTS.FULFILLMENT_METHOD_BOPIS:
      return PickupInStoreIcon;
    case FULFILLMENTS.FULFILLMENT_METHOD_APPLIANCE:
      return ApplianceIcon;
    case FULFILLMENTS.FULFILLMENT_METHOD_MISC:
      return InStoreIcon;
    default: {
      console.error(
        `FulfillmentType: ${fulfillmentType} Unknown. Cannot set proper Icon Defaulting to STH`
      );
      return ShippingIcon;
    }
  }
};

export const setFulfillmentLocation = (fulfillment: Object) => {
  const { fulfillmentMethod } = fulfillment;

  const fulfillmentMeta = {
    shiptohome: [null],
    buyonlinedeliverfromstore: [null],
    appliance: [null],
    buyonlinepickupinstore: ['pickUpDetails', 'store', 'number'],
    buyonlineshiptostore: [
      'bossShippingAndHandling',
      'pickUpDetails',
      'store',
      'number',
    ],
  }[fulfillmentMethod.toLowerCase()];

  return safelyAccessNestedProperties(fulfillmentMeta, fulfillment);
};
